/**
 * API client methods for Epic 19 + Epic 20 mentor AI surfaces.
 *
 * Endpoints are implemented against the backend contract spec.
 * The backend may not be deployed yet; these calls will 404 until it is.
 * All methods follow the existing pattern in this codebase:
 *   const { data } = await apiClient.get(...)
 *   return data;
 */

import { apiClient } from './client';

// ---------------------------------------------------------------------------
// Epic 19 — Mentor Read Surface
// ---------------------------------------------------------------------------

/**
 * 19-1-a: Get all students assigned to the current mentor/teacher.
 *
 * GET /mentor/students
 *
 * @param {{ courseId?, riskBand?, lastActivityRange?, locale?, sort?, dir?, page?, limit? }} params
 * @returns {{ data: StudentStatusRow[], meta: { total, page, limit, totalPages } }}
 *
 * StudentStatusRow shape:
 *   { id, name, email, riskScore: number|null, riskBand: 'high'|'med'|'low'|null,
 *     topRiskFactor: string|null, riskScoreAt: string|null, lastActivityAt: string|null,
 *     locale: string }
 */
export async function getMentorStudents(params = {}) {
  const { data } = await apiClient.get('/mentor/students', { params });
  return data;
}

/**
 * 19-2-a: Get assessment and recommendations for a specific student.
 *
 * GET /mentor/students/:studentId/assessment
 *
 * @param {string} studentId
 * @returns {AssessmentDetailDto}
 *
 * AssessmentDetailDto shape:
 *   {
 *     student: { id, name },
 *     assessment: {
 *       id, riskScore, riskBand, riskFactors: string[], narrativeForMentor: string,
 *       suggestedIntervention: string|null, trigger: string, locale: string,
 *       createdAt: string
 *     } | null,
 *     recommendations: RecommendationDto[]
 *   }
 *
 * RecommendationDto shape:
 *   { id, targetType: 'MODULE'|'LESSON'|'QUIZ', targetId,
 *     targetName: string|null,  // 'Course > Module > Lesson'; a quiz is named after its lesson
 *     reasoning, priority: number,
 *     skills: { slug, name }[],  // skillsAddressed (the bare slugs) comes along
 *     state: 'PENDING'|'SEEN'|'ACCEPTED'|'DISMISSED'|'COMPLETED'|'SUPERSEDED'|'EXPIRED',
 *     sourceType: 'AI'|'MENTOR'|'AI_BOOTSTRAP',
 *     createdByUser: { id, name, avatarUrl: string|null } | null,
 *     mentorSuppressedAt: string|null, mentorSuppressedReason: string|null, createdAt: string }
 *
 * The server never sends state 'MENTOR_SUPPRESSED': suppressing only stamps
 * mentorSuppressedAt and leaves state as it was. The page derives that state from the stamp.
 */
export async function getStudentAssessment(studentId) {
  const { data } = await apiClient.get(`/mentor/students/${studentId}/assessment`);
  return data;
}

/**
 * 19-3: Get risk score history for a student (for the trend chart).
 *
 * GET /mentor/students/:studentId/assessment-history?limit=12
 *
 * @param {string} studentId
 * @param {{ limit?: number }} params  Default limit: 10. Max: 52.
 * @returns {{ history: HistoryPoint[] }}
 *
 * HistoryPoint shape:
 *   { id, riskScore: number, createdAt: string, trigger: string }
 *   Ordered chronologically ascending (oldest first).
 */
export async function getStudentRiskHistory(studentId, params = {}) {
  const { data } = await apiClient.get(`/mentor/students/${studentId}/assessment-history`, { params });
  return data;
}

// ---------------------------------------------------------------------------
// Epic 20 — Mentor Actions
// ---------------------------------------------------------------------------

/**
 * 20-1-a: Suppress a recommendation.
 *
 * POST /mentor/recommendations/:recommendationId/suppress
 *
 * @param {string} recommendationId
 * @param {string} reason  The mentor's suppression reason (max 500 chars).
 * @returns {{ id: string, mentorSuppressedAt: string, mentorSuppressedReason: string|null }}
 *   (the full recommendation row; its state is not changed)
 */
export async function suppressRecommendation(recommendationId, reason) {
  const { data } = await apiClient.post(`/mentor/recommendations/${recommendationId}/suppress`, { reason });
  return data;
}

/**
 * 20-2-a: Create a manual recommendation for a student.
 *
 * POST /mentor/students/:studentId/recommendations
 *
 * @param {string} studentId
 * @param {{ targetType: 'MODULE'|'LESSON'|'QUIZ', targetId: string,
 *            reasoning: string, skillSlugs: string[], priority?: number }} body
 *   priority is 1 to 5 and defaults to 1; skillSlugs come from GET /skills.
 * @returns {RecommendationDto}  The newly created recommendation (201 response body).
 */
export async function createMentorRecommendation(studentId, body) {
  const { data } = await apiClient.post(`/mentor/students/${studentId}/recommendations`, body);
  return data;
}

/**
 * Get the student's enrolled course content for the recommendation target picker.
 *
 * GET /mentor/students/:studentId/enrolled-content
 *   Returns what a recommendation may point at: the published courses the student is
 *   enrolled in, minus the lessons the student already completed.
 *
 * @param {string} studentId
 * @returns {{ courses: EnrolledCourse[] }}
 *
 * EnrolledCourse shape:
 *   { id, title,
 *     modules: [{ id, title, lessons: [{ id, title }], quizzes: [{ id, title }] }],
 *     lessons: [{ id, title }],   // lessons that sit outside any module
 *     quizzes: [{ id, title }] }  // their quizzes; a quiz is titled after its lesson
 */
export async function getStudentEnrolledContent(studentId) {
  const { data } = await apiClient.get(`/mentor/students/${studentId}/enrolled-content`);
  return data;
}
